import React from "react";

import clsx from "clsx";
import moment from "moment";
import { type ColumnShape } from "react-base-table";

import {
  type AugmentedFactColumn,
  type AugmentedTableRow,
} from "@components/reports/virtualized-table";
import { GenericTooltip } from "@components/tooltip-container";
import { type User } from "@internal/user";
import { type GetCustomerReportV2_me_customerReportV2_rows } from "@north-beam/nb-client-side";
import {
  CUSTOMER_LTV_PERIODS,
  formatting,
  metrics,
  type CategoricalBreakdownConfig,
} from "@north-beam/nb-common";
import { getCellIntensityColor } from "@pages/customers/report";
import {
  SumModeEnum,
  type SumMode,
} from "@pages/customers/report/sum-mode-select";

import { METRIC_FORMATS, type MetricName } from "../../constants";
import { type CustomerLtvV2Data } from "../../context";

export function buildTable({
  user,
  breakdownConfigs,
  breakdown,
  metric,
  rollupMode,
  sumMode,
  data,
}: {
  user: User;
  breakdownConfigs?: CategoricalBreakdownConfig[];
  breakdown?: string;
  metric: MetricName;
  rollupMode: string;
  sumMode: SumMode;
  data: CustomerLtvV2Data | null;
}) {
  if (!breakdown || !data) {
    return null;
  }

  const selectedBreakdown = breakdownConfigs?.find((e) => e.key === breakdown);

  const dimensionColumn = {
    name: selectedBreakdown?.name || "",
    key: "breakdown",
  };

  const factColumns: AugmentedFactColumn[] = [
    {
      name: "Customers",
      key: "totalNumCustomers",
      renderHeader: () =>
        createHeader("Customers", "Total number of customers in this cohort."),
      renderCell: ({ row, column }) =>
        createCell(
          row,
          column,
          data.minValues?.totalNumCustomers || 0,
          data.maxValues?.totalNumCustomers || 0,
          breakdown,
        ),
    },
  ];

  CUSTOMER_LTV_PERIODS.forEach((period) => {
    const columnName = `metric_${period}` as const;
    let name = "";
    let tooltip = "";

    if (rollupMode === "Marginal") {
      const startDay = period === 30 ? 2 : period - 29;
      name = period === 0 ? "Day 1" : `Day ${startDay}—${period}`;
      tooltip =
        period === 0
          ? "Activity in the first day."
          : `Activity between day ${startDay} and day ${period}.`;
    } else if (rollupMode === "Cumulative") {
      name = period === 0 ? "Day 1" : `Day 1—${period}`;
      tooltip =
        period === 0
          ? "Activity in the first day."
          : `Activity up until day ${period}.`;
    } else {
      name = period === 0 ? "Day 1" : `Day 2—${period}`;
      tooltip =
        period === 0
          ? "Activity in the first day."
          : `Activity between day 2 and day ${period}.`;
    }

    factColumns.push({
      name,
      key: columnName,
      renderHeader: () => createHeader(name, tooltip),
      renderCell: ({ row, column }) =>
        createCell(
          row,
          column,
          data.minValues?.[columnName] || 0,
          data.maxValues?.[columnName] || 0,
          breakdown,
        ),
    });
  });

  const { exportValues, displayValues } = getColumnValues(
    data.summaryRow,
    user,
    metric,
    sumMode,
  );

  const stickyRows: AugmentedTableRow[] = [
    {
      id: "row-grand-total",
      export: {
        breakdown: data.summaryRow.breakdown,
        totalNumCustomers: data.summaryRow.totalNumCustomers,
        ...exportValues,
      },
      display: {
        breakdown: data.summaryRow.breakdown,
        totalNumCustomers: data.summaryRow.totalNumCustomers.toString(),
        ...displayValues,
      },
      link: {} as Record<string, string>,
      changeFractions: {},
      rowMetadata: {},
      cellHoverHTML: {},
      children: [],
    },
  ];

  const rows: AugmentedTableRow[] = [];

  data.rows.forEach((row) => {
    const { exportValues, displayValues } = getColumnValues(
      row,
      user,
      metric,
      sumMode,
    );

    rows.push({
      id: `row-${row.breakdown}`,
      export: {
        breakdown: row.breakdown,
        totalNumCustomers: row.totalNumCustomers,
        ...exportValues,
      },
      display: {
        breakdown: row.breakdown,
        totalNumCustomers: row.totalNumCustomers.toString(),
        ...displayValues,
      },
      link: {} as Record<string, string>,
      changeFractions: {},
      rowMetadata: {},
      cellHoverHTML: {},
      children: [],
    });
  });

  return { dimensionColumn, factColumns, stickyRows, rows };
}

function getColumnValues(
  row: GetCustomerReportV2_me_customerReportV2_rows,
  user: User,
  metric: MetricName,
  sumMode: SumMode,
) {
  let format: metrics.MetricType = METRIC_FORMATS[metric];

  if (
    SumModeEnum[sumMode] === SumModeEnum.AveragedPerCustomer &&
    format === metrics.MetricTypes.INTEGER
  ) {
    format = metrics.MetricTypes.DECIMAL;
  }

  const exportValues: Record<string, any> = {};
  const displayValues: Record<string, string> = {};

  CUSTOMER_LTV_PERIODS.forEach((period) => {
    const columnName = `metric_${period}` as const;

    exportValues[columnName] = row[columnName];
    displayValues[columnName] = formatting.formatMetric(
      row[columnName],
      format,
      {
        code: user.currencies.currentCurrency,
      },
    );
  });

  return { exportValues, displayValues };
}

function createHeader(name: string, tooltip: string) {
  return (
    <div className="flex items-center justify-center w-full">
      <GenericTooltip noInfoCircle content={tooltip}>
        {name}
      </GenericTooltip>
    </div>
  );
}

function createCell(
  row: AugmentedTableRow,
  column: ColumnShape<AugmentedTableRow>,
  colMin: number,
  colMax: number,
  breakdown: string,
) {
  let cellIsDisabled = false;

  if (["first_order_month", "first_order_week_monday"].includes(breakdown)) {
    const today = moment();
    const rowStartDateString = row.id.replace("row-", "").slice(0, 10);
    const daysUntilToday = today.diff(moment(rowStartDateString), "day");
    const days = parseInt(column.key.toString().match(/\d+$/)?.[0] || "");

    if (days > daysUntilToday) {
      cellIsDisabled = true;
    }
  }

  const backgroundColor =
    row.id === "row-grand-total"
      ? "transparent"
      : getCellIntensityColor(row.export[column.key], colMin, colMax);

  return (
    <div
      className={clsx("w-full h-full px-3 flex items-center justify-end", {
        "table-cell-disabled": cellIsDisabled,
      })}
      style={{ backgroundColor }}
    >
      {cellIsDisabled ? "" : row.display[column.key]}
    </div>
  );
}
